// 용사의 시련(3개월 C7, 첫날 콘텐츠 — 정본 docs/PROGRESSION-720H.md 12절). 순수 데이터·판정(DOM·저장소 없음).
//
// 지역1 노말·지역2 노말·하드 지역1의 스테이지마다 시련이 하나씩(시련판 3개 × 10 = 30개) 있다. 스테이지를 처음 깨면 그 스테이지의 시련이 열리고,
// 시련 판은 같은 지도를 조건(속공·무혈·연격·완벽)을 걸고 다시 도는 도전 모드다(디아블로 도전 균열처럼 경험치·카드·첫 클리어·진행 기록은 없고 시련 보상만).
// 경험치를 주지 않는 이유: 시련 재도전이 사냥(EXP)을 대신하면 첫날 레벨 곡선(xpTable)이 빨라지고, 시련은 '손맛 숙련' 목표로 남아야 한다.
// 시련판 10개를 모두 이루면 시련 상자(큰 보상)를 받는다. 보상 지급은 고정 id(골드 = save.v1 보너스 영수증, 강화석 = 장비 장부 연산)라 몇 번을 불러도 한 번이다.
//
// 목표 수치는 헤드리스 정상 입력 봇 측정(qa/3month/c7/trialbot.json, 시드 3개 중앙값)에서 사람 기준으로 옮겼다:
//  - 속공: 봇 시간 ÷ 사람 보정 0.6(PROGRESSION-720H 11절) × 0.8 — 사람 평균보다 20% 빨라야 한다(5초 단위 올림)
//  - 연격: 봇 최대 콤보 × 0.5(사람은 스킬을 쉬고 길을 돈다, 10 단위 내림)
//  - 무혈: 판 내내 HP가 목표 % 밑으로 떨어지지 않기(최저 HP) — 일반 70%·하드 65%. 봇 최저 HP는 85~97%(지역보스 39~79%),
//    표준 플레이어는 판당 15~30%를 잃는다(hunt-bot c018) — 사람에게 반반쯤인 문턱
//  - 헬(C10-4): 같은 식, 봇 표는 qa/3month/c10/hell-trialbot.json. 무혈 문턱 60% — 헬 무혈 스테이지(n-2·n-7) 봇 최저 HP 79~100%(지역6에서 79%)라
//    일반 판(봇 85~97% → 70%)보다 10%p 낮춘다. 헬 지역1~3은 이 픽스처에서 거의 무위협이라 속공·연격이 주 난관이다
//  - 완벽: 별 3개(클리어·5분 안·HP 50% 이상, flow.ts StarBit) — 중간·지역보스 스테이지
import type { AnyStageId } from './types';
import { DIFFICULTY_ILVL_ADD, DIFFICULTY_LOOT, REGION_GOLD, stoneTierForIlvl, type StoneTier } from './economy';
import { REGIONS_90D } from './world';
import type { RegionNo } from './types';

/** 헬 시련판(C10-4): 헬 지역 n의 스테이지 10개. x = 헬 스테이지 id 머리(x3-s05 = 헬 3-5)와 같다 */
export type HellTrialBoardId = `x${RegionNo}`;
export type TrialBoardId = 'r1' | 'r2' | 'h1' | HellTrialBoardId;
export type TrialDiff = 'normal' | 'hard' | 'hell';
export type TrialKind = 'speed' | 'guard' | 'combo' | 'perfect';

export interface TrialReward { readonly gold: number; readonly stones: number; readonly tier: StoneTier }
export interface TrialDef {
  readonly id: string;
  readonly board: TrialBoardId;
  /** 그 시련판 안 순서(1..10 = 스테이지 번호) */
  readonly index: number;
  readonly stage: AnyStageId;
  readonly kind: TrialKind;
  /** speed = 제한 초, guard = 판 중 최저 HP %, combo = 최대 콤보, perfect = 별 수(3) */
  readonly target: number;
  readonly name: string;
  readonly hint: string;
  readonly reward: TrialReward;
}
export interface TrialBoardDef {
  readonly id: TrialBoardId;
  readonly name: string;
  /** 노말이 아닌 판(하드·헬). UI 색조용 — 난이도 판정은 diff */
  readonly hard: boolean;
  readonly diff: TrialDiff;
  readonly region: RegionNo;
  /** 10개 모두 이뤘을 때 상자 */
  readonly chest: TrialReward;
}

/** 봇 측정 중앙값(qa/3month/c7/trialbot.json summary: clearSec, maxCombo). 측정을 다시 하면 이 표만 바꾼다 */
export const TRIAL_BOT: Readonly<Record<TrialBoardId, readonly (readonly [number, number])[]>> = {
  r1: [[84.4, 140], [77.5, 143], [79.0, 140], [87.4, 162], [99.6, 181], [92.3, 185], [101.7, 201], [97.2, 214], [98.0, 226], [122.5, 239]],
  r2: [[64.6, 63], [69.4, 60], [62.4, 69], [73.8, 97], [73.2, 101], [72.0, 121], [83.9, 140], [84.1, 147], [81.6, 103], [136.6, 193]],
  h1: [[74.6, 130], [77.0, 152], [77.8, 167], [83.1, 216], [106.8, 219], [91.5, 207], [101.9, 259], [98.7, 275], [98.0, 267], [148.8, 286]],
  // 헬(C10-4): qa/3month/c10/hell-trialbot.json(tools/qa/3month-c10-hell-trialbot.ts, Lv77~98(픽스처 레벨 검증)·지역6 영웅 +11, 시드 3개 중앙값, 180판 실패 0)
  x1: [[52.9, 71], [60.3, 73], [60.5, 78], [57.7, 85], [58.7, 88], [61.1, 86], [64.6, 95], [66, 94], [62.2, 93], [68.8, 122]],
  x2: [[43.4, 24], [45.2, 35], [44.4, 40], [50.9, 52], [41.8, 58], [43.2, 51], [45.5, 62], [48.3, 65], [47.2, 69], [63.1, 76]],
  x3: [[78.3, 71], [76.3, 94], [82.7, 74], [79.3, 137], [94.3, 133], [85.9, 160], [87.8, 162], [90.7, 185], [94.7, 217], [124.8, 240]],
  x4: [[85.6, 111], [90.5, 163], [95.6, 170], [115.9, 208], [124, 237], [107.2, 273], [112.6, 292], [121, 382], [123.6, 364], [194.7, 408]],
  x5: [[113.3, 246], [114, 268], [123.7, 311], [131.7, 345], [178.5, 390], [135.4, 394], [150.1, 482], [152.6, 467], [153.2, 554], [367.8, 637]],
  x6: [[136.5, 357], [148.7, 406], [149.7, 494], [188.2, 564], [302.1, 596], [191.8, 684], [193.5, 784], [209, 857], [199.1, 883], [652.2, 1032]],
};
/** 사람 보정(PROGRESSION-720H 11절 KILL_RATE_HUMAN_FACTOR와 같은 값)·속공 여유·연격 비율 */
export const TRIAL_TUNE = { human: 0.6, speedShare: 0.8, comboShare: 0.5, guardNormal: 70, guardHard: 65, guardHell: 60, perfectStars: 3 } as const;
/** 스테이지 번호(1..10) → 시련 종류. 5·10(중간·지역보스)은 완벽 */
export const TRIAL_PATTERN: readonly TrialKind[] = ['speed', 'guard', 'combo', 'speed', 'perfect', 'combo', 'guard', 'speed', 'combo', 'perfect'];

const board = (id: TrialBoardId, name: string, diff: TrialDiff, region: RegionNo): TrialBoardDef =>
  ({ id, name, hard: diff !== 'normal', diff, region, chest: chestOf(region, diff) });
export const HELL_TRIAL_REGIONS: readonly RegionNo[] = [1, 2, 3, 4, 5, 6];
export const TRIAL_BOARDS: readonly TrialBoardDef[] = [
  board('r1', '이끼 유적의 시련', 'normal', 1),
  board('r2', '잿불 협곡의 시련', 'normal', 2),
  board('h1', '하드 · 이끼 유적의 시련', 'hard', 1),
  // 헬 시련판(C10-4, 첫 달 엔드게임 숙련 목표 — 디아블로 도전 균열·던파 시련의 '같은 지도, 더 높은 조건'): 헬 지역 n-1을 처음 깨면 열린다
  ...HELL_TRIAL_REGIONS.map((n) => board(`x${n}`, '헬 · ' + REGIONS_90D[n - 1].name + '의 시련', 'hell', n)),
];

/**
 * 시련 하나 보상. 노말·하드(C7): 골드 = 지역 일반 판 기본 골드(500·1100) × 1, 하드 × 3 / 강화석 2(하드 3).
 * 헬(C10-4): 골드 = 그 지역 클리어 골드(economy REGION_GOLD.clear) × 헬 골드 배율(DIFFICULTY_LOOT.hell.gold 4.8) / 강화석 3.
 * 등급은 그 지역 아이템 레벨 + 난이도 가산(헬 +20 — 헬 판 드롭과 같은 강화석 등급). 판당 한 번(고정 id)이라 반복 파밍이 아니다
 */
function rewardOf(region: RegionNo, diff: TrialDiff): TrialReward {
  if (diff === 'hell') return { gold: Math.round(REGION_GOLD[region - 1].clear * DIFFICULTY_LOOT.hell.gold), stones: 3, tier: tierOf(region, diff) };
  const base = region === 1 ? 500 : 1100;
  return { gold: base * (diff === 'hard' ? 3 : 1), stones: diff === 'hard' ? 3 : 2, tier: tierOf(region, diff) };
}
function chestOf(region: RegionNo, diff: TrialDiff): TrialReward {
  const r = rewardOf(region, diff);
  return { gold: r.gold * 6, stones: r.stones * 5, tier: r.tier };
}
function tierOf(region: RegionNo, diff: TrialDiff): StoneTier {
  return stoneTierForIlvl(REGIONS_90D[region - 1].levels[0] + DIFFICULTY_ILVL_ADD[diff]);
}

const up5 = (n: number) => Math.ceil(n / 5) * 5;
const down10 = (n: number) => Math.max(10, Math.floor(n / 10) * 10);
const KIND_NAME: Readonly<Record<TrialKind, string>> = { speed: '속공', guard: '무혈', combo: '연격', perfect: '완벽한 승리' };

/** 판 id → 난이도(TRIAL_BOARDS를 만들기 전에도 쓰이므로 id 머리로 정한다: h = 하드, x = 헬) */
function boardDiff(board: TrialBoardId): TrialDiff { return board[0] === 'x' ? 'hell' : board[0] === 'h' ? 'hard' : 'normal'; }
/** 시련 목표 수치(위 머리말의 식) */
export function trialTarget(board: TrialBoardId, index: number, kind: TrialKind): number {
  const [sec, combo] = TRIAL_BOT[board][index - 1];
  if (kind === 'speed') return up5((sec / TRIAL_TUNE.human) * TRIAL_TUNE.speedShare);
  if (kind === 'combo') return down10(combo * TRIAL_TUNE.comboShare);
  if (kind === 'guard') { const d = boardDiff(board); return d === 'hell' ? TRIAL_TUNE.guardHell : d === 'hard' ? TRIAL_TUNE.guardHard : TRIAL_TUNE.guardNormal; }
  return TRIAL_TUNE.perfectStars;
}
export function secText(sec: number): string {
  const m = Math.floor(sec / 60), s = sec % 60;
  return m > 0 ? m + '분' + (s ? ' ' + s + '초' : '') : s + '초';
}
function nameOf(kind: TrialKind, label: string, target: number, boss: string): string {
  if (kind === 'speed') return `${label} 속공 · ${secText(target)} 안에`;
  if (kind === 'guard') return `${label} 무혈 · HP ${target}% 이상`;
  if (kind === 'combo') return `${label} 연격 · ${target}콤보`;
  return `${label} 완벽한 승리 · ${boss}`;
}
function hintOf(kind: TrialKind, target: number): string {
  if (kind === 'speed') return `${secText(target)} 안에 마지막 적까지 쓰러뜨려요 · 시간이 넘으면 시련 실패`;
  if (kind === 'guard') return `판 내내 HP ${target}% 이상을 지켜요 · 그 밑으로 한 번이라도 떨어지면 시련 실패`;
  if (kind === 'combo') return `한 판 최대 콤보 ${target} 이상 · 콤보는 2.5초 안에 다음 타격을 이어야 유지돼요`;
  return '별 3개: 5분 안에, HP 50% 이상으로 보스를 쓰러뜨려요';
}

const DIFF_HEAD: Readonly<Record<TrialDiff, string>> = { normal: 'r', hard: 'h', hell: 'x' };
const DIFF_LABEL: Readonly<Record<TrialDiff, string>> = { normal: '', hard: '하드 ', hell: '헬 ' };
/** 판 결과 영수증의 지역 키(save.v1 RegionId) */
export const trialRegionKey = (b: TrialBoardDef): `r${RegionNo}` => `r${b.region}`;

function boardTrials(b: TrialBoardDef): TrialDef[] {
  const reg = REGIONS_90D[b.region - 1];
  const out: TrialDef[] = [];
  for (let i = 1; i <= 10; i++) {
    const kind = TRIAL_PATTERN[i - 1], target = trialTarget(b.id, i, kind);
    const stage = `${DIFF_HEAD[b.diff]}${b.region}-s${String(i).padStart(2, '0')}` as AnyStageId;
    const label = `${DIFF_LABEL[b.diff]}${b.region}-${i}`;
    const boss = i === 5 ? reg.midBoss : i === 10 ? reg.finalBoss : '';
    out.push({ id: `trial-${b.id}-${String(i).padStart(2, '0')}`, board: b.id, index: i, stage, kind, target, name: nameOf(kind, label, target, boss), hint: hintOf(kind, target), reward: rewardOf(b.region, b.diff) });
  }
  return out;
}

export const TRIALS: readonly TrialDef[] = TRIAL_BOARDS.flatMap(boardTrials);
const BY_ID = new Map(TRIALS.map((t) => [t.id, t]));
export function trialDef(id: unknown): TrialDef | null { return typeof id === 'string' ? BY_ID.get(id) ?? null : null; }
export function trialBoard(id: TrialBoardId): TrialBoardDef { return TRIAL_BOARDS.find((b) => b.id === id)!; }
/** 명령·저장에서 온 값이 시련판 id인가 */
export function isTrialBoardId(v: unknown): v is TrialBoardId { return typeof v === 'string' && TRIAL_BOARDS.some((b) => b.id === v); }
export function boardTrialsOf(id: TrialBoardId): readonly TrialDef[] { return TRIALS.filter((t) => t.board === id); }
export const trialKindName = (k: TrialKind): string => KIND_NAME[k];

/** 지급 고정 id. 골드 = save.v1 보너스 영수증 id(완료 기록도 겸한다), 강화석 = 장비 장부 연산 id(골드 id + ':s') */
export const trialRewardId = (id: string): string => 'trial:' + id;
export const trialChestId = (board: TrialBoardId): string => 'trialchest:' + board;

/** 판 결과(시련 판정에 쓰는 값만) */
/** hpLowPct = 판 중 최저 HP %(무혈 판정) */
export interface TrialRun { readonly cleared: boolean; readonly clearSec: number; readonly maxCombo: number; readonly hpLowPct: number; readonly stars: number }
/** 시련을 이뤘는가 */
export function trialMet(t: Pick<TrialDef, 'kind' | 'target'>, r: TrialRun): boolean {
  if (!r.cleared) return false;
  if (t.kind === 'speed') return r.clearSec <= t.target;
  if (t.kind === 'guard') return r.hpLowPct >= t.target;
  if (t.kind === 'combo') return r.maxCombo >= t.target;
  return r.stars >= t.target;
}
/**
 * 판 도중 이미 실패가 정해졌는가(속공 = 제한 시간 넘김, 무혈 = HP가 한 번이라도 목표 밑). 연격·완벽은 끝나야 안다(완벽의 HP 별은 클리어 순간 HP라 도중에 떨어져도 회복할 수 있다).
 * 실패가 정해지면 판을 바로 끝내 재도전 시간을 아낀다(game/trialRun.ts)
 */
export function trialFailedEarly(t: Pick<TrialDef, 'kind' | 'target'>, elapsedSec: number, hpPct: number): boolean {
  if (t.kind === 'speed') return elapsedSec > t.target;
  if (t.kind === 'guard') return hpPct < t.target;
  return false;
}

/** 퀘스트 창·결과 화면이 읽는 시련 상태(값이 바뀔 때만 rev가 오른다) */
export interface TrialsView {
  /** 이룬 시련 id */
  readonly done: readonly string[];
  /** 받은 시련 상자(시련판 id) */
  readonly chests: readonly string[];
  /** 열린 시련 id(스테이지를 처음 깸) */
  readonly open: readonly string[];
  /** 지금 시련 판이면 그 시련 id와 진행 문구, 아니면 '' */
  readonly live: { readonly id: string; readonly status: string; readonly danger: boolean };
  readonly rev: number;
}
